import React, { useState } from "react";

import { Autocomplete, Button, Chip, Stack, TextField } from "@mui/material";
import { useTaskDealsSelect } from "store";
import { DealBase } from "store/types";

interface Props {
  currentDealIds: number[];
  onSubmit: (result: { deals: DealBase[] }) => void;
  onCancel: () => void;
}

// Multi-select deal picker used by the cloud-mirror upload flow, where a file can belong to several deals.
export const DealsMultiPrompt: React.FC<Props> = ({ currentDealIds, onSubmit, onCancel }) => {
  const deals = useTaskDealsSelect();

  const [selected, setSelected] = useState<DealBase[]>(deals.filter((d) => currentDealIds.includes(d.id)));

  return (
    <Stack sx={{ padding: "24px", gap: "20px", minWidth: "427px" }}>
      <Autocomplete<DealBase, true>
        multiple
        disableCloseOnSelect
        options={deals}
        value={selected}
        onChange={(_, value) => setSelected(value)}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label="Deals" />}
        renderTags={(tagValue, getTagProps) => (
          <div style={{ maxHeight: 76, overflowY: "auto", overflowX: "hidden" }}>
            {tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.id} label={option.title} sx={{ maxWidth: 220 }} />
            ))}
          </div>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id} style={{ fontSize: "16px" }}>
            {option.title}
          </li>
        )}
        slotProps={{
          popper: {
            placement: "top-start",
            modifiers: [{ name: "flip", enabled: false }]
          },
          listbox: {
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: "16px",
                "&:hover, &.Mui-focused": { bgcolor: "#edf1f9" },
                "&[aria-selected='true']": { bgcolor: "#edf1f9", "&.Mui-focused": { bgcolor: "#edf1f9" } }
              }
            }
          }
        }}
        sx={{
          maxWidth: 380,
          "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
            borderRadius: "8px",
            "& fieldset": { borderColor: "#7b92a9" }
          }
        }}
      />
      <Stack direction="row" justifyContent="flex-end" gap="8px">
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            borderRadius: "32px",
            borderColor: "#7b92a9",
            color: "#071f35",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            "&:hover": { borderColor: "#7b92a9" }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected.length}
          onClick={() => onSubmit({ deals: selected })}
          sx={{
            borderRadius: "32px",
            bgcolor: "#071f35",
            color: "white",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            "&:hover": { bgcolor: "#0a2a45" },
            "&:disabled": { bgcolor: "#bac7ce", color: "white" }
          }}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};
